import React, { useState } from 'react';
import { ethers } from 'ethers';

function UserPanel({ 
  contract, 
  account, 
  userInfo, 
  usdtBalance,
  isRegistered,
  onRefresh 
}) {
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatUSDT = (value) => {
    if (!value) return '0.00';
    return parseFloat(ethers.utils.formatUnits(value, 18)).toFixed(2);
  };

  const handleRegister = async () => {
    setRegisterLoading(true);
    setError('');
    try {
      const tx = await contract.register();
      await tx.wait();
      setSuccess('Registration successful!');
      onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      if (err.code === 'ACTION_REJECTED' || err.code === 4001) {
        setError('Transaction rejected');
      } else if (err.reason) {
        setError(err.reason);
      } else {
        setError('Registration failed');
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setError('Enter valid amount');
      return;
    }

    const amountWei = ethers.utils.parseUnits(withdrawAmount, 18);
    const claimable = userInfo?.claimableWinnings || ethers.BigNumber.from(0);

    if (amountWei.gt(claimable)) {
      setError('Exceeds claimable balance');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const tx = await contract.withdraw(amountWei);
      await tx.wait();
      setWithdrawAmount('');
      setSuccess('Withdrawal successful!');
      onRefresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      if (err.code === 'ACTION_REJECTED' || err.code === 4001) {
        setError('Transaction rejected');
      } else if (err.reason) {
        setError(err.reason);
      } else {
        setError('Withdrawal failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const setMaxWithdraw = () => {
    if (userInfo?.claimableWinnings) {
      setWithdrawAmount(ethers.utils.formatUnits(userInfo.claimableWinnings, 18));
    }
  };

  if (!account) {
    return (
      <div className="user-panel">
        <div className="panel-header">
          <h2>💼 My Account</h2>
        </div>
        <div className="empty-state">
          <div className="empty-icon">🎮</div>
          <p>Connect your wallet to view your account</p>
        </div>
      </div>
    );
  }

  const claimable = userInfo?.claimableWinnings || ethers.BigNumber.from(0);
  const totalInvested = userInfo?.totalInvested || ethers.BigNumber.from(0);
  const totalWithdrawn = userInfo?.totalWithdrawn || ethers.BigNumber.from(0);

  const withdrawAmountWei = withdrawAmount ? 
    ethers.utils.parseUnits(withdrawAmount || '0', 18) : ethers.BigNumber.from(0);
  const feeAmount = withdrawAmountWei.mul(10).div(100);
  const netAmount = withdrawAmountWei.sub(feeAmount);

  return (
    <div className="user-panel">
      <div className="panel-header">
        <h2>💼 My Account</h2>
        {isRegistered && <span className="registered-badge">✓ Registered</span>}
      </div>

      {!isRegistered ? (
        <div className="register-section">
          <div className="register-icon">🚀</div>
          <h3>Join the Game</h3>
          <p>Register to start playing Head or Tails</p>
          {error && <div className="error-msg">{error}</div>}
          {success && <div className="success-msg">{success}</div>}
          <button 
            className="btn btn-register"
            onClick={handleRegister}
            disabled={registerLoading}
          >
            {registerLoading ? (
              <><span className="spinner"></span> Registering...</>
            ) : (
              <>✨ Register Now</>
            )}
          </button>
        </div>
      ) : (
        <>
          {/* Balance Cards */}
          <div className="balance-grid">
            <div className="balance-card wallet">
              <div className="balance-icon">👛</div>
              <div className="balance-info">
                <span className="label">Wallet Balance</span>
                <span className="value">{formatUSDT(usdtBalance)} USDT</span>
              </div>
            </div>
            <div className="balance-card winnings">
              <div className="balance-icon">🏆</div>
              <div className="balance-info">
                <span className="label">Claimable Winnings</span>
                <span className="value">{formatUSDT(claimable)} USDT</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="stats-row">
            <div className="stat">
              <span className="stat-label">Total Invested</span>
              <span className="stat-value">{formatUSDT(totalInvested)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Withdrawn</span>
              <span className="stat-value">{formatUSDT(totalWithdrawn)}</span>
            </div>
          </div>

          {/* Withdraw Section */}
          <div className="withdraw-section">
            <h3>💸 Withdraw</h3>
            <div className="input-wrapper">
              <input
                type="number"
                placeholder="Amount to withdraw"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                disabled={loading || claimable.isZero()}
              />
              <button 
                className="max-btn" 
                onClick={setMaxWithdraw}
                disabled={claimable.isZero()}
              >
                MAX
              </button>
            </div>

            {withdrawAmount && parseFloat(withdrawAmount) > 0 && (
              <div className="withdraw-breakdown">
                <div className="breakdown-row">
                  <span>Amount</span>
                  <span>{parseFloat(withdrawAmount).toFixed(2)} USDT</span>
                </div>
                <div className="breakdown-row fee">
                  <span>Fee (10%)</span>
                  <span>-{formatUSDT(feeAmount)} USDT</span>
                </div>
                <div className="breakdown-row net">
                  <span>You Receive</span>
                  <span>{formatUSDT(netAmount)} USDT</span>
                </div>
              </div>
            )}

            {error && <div className="error-msg">{error}</div>}
            {success && <div className="success-msg">{success}</div>}

            <button 
              className="btn btn-withdraw"
              onClick={handleWithdraw}
              disabled={loading || claimable.isZero()}
            >
              {loading ? (
                <><span className="spinner"></span> Processing...</>
              ) : (
                <>Withdraw</>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default UserPanel;
